/**
 * ClerkManager - Singleton manager for Clerk authentication
 * Provides centralized Clerk instance management with retry logic
 */

import { clerkInstance, clerkUser, isClerkLoaded, clerkError } from '../stores/clerk.js';
import { ExponentialBackoff } from './backoff.js';

/**
 * ClerkManager class for managing Clerk SDK lifecycle
 * Implements singleton pattern to ensure single Clerk instance
 *
 * @example
 * import { clerkManager } from './clerk-manager.js';
 *
 * // Load Clerk with automatic retry
 * const clerk = await clerkManager.load();
 *
 * // Get auth headers for API calls
 * const headers = await clerkManager.getAuthHeaders();
 */
export class ClerkManager {
  constructor() {
    this.loadPromise = null;
    this.backoff = new ExponentialBackoff(100, 5000, 8);
  }

  /**
   * Load Clerk SDK with exponential backoff retry logic
   * Returns cached instance if already loaded
   * Returns same promise if currently loading
   *
   * @returns {Promise<object>} The Clerk instance
   * @throws {Error} When max retry attempts exceeded
   */
  load() {
    // Return cached instance if already loaded
    const cached = clerkInstance.get();
    if (cached) {
      return Promise.resolve(cached);
    }

    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Start new load operation
    this.loadPromise = this._loadWithRetry()
      .finally(() => {
        this.loadPromise = null;
      });

    return this.loadPromise;
  }

  /**
   * Internal method to load Clerk with retry logic
   * @private
   */
  async _loadWithRetry() {
    try {
      const clerk = await this.backoff.retry(async () => {
        if (!window.Clerk) {
          throw new Error('Clerk SDK not loaded');
        }
        return window.Clerk;
      });

      // Load Clerk instance
      await clerk.load();

      // Update stores
      clerkInstance.set(clerk);
      clerkUser.set(clerk.user);
      isClerkLoaded.set(true);
      clerkError.set(null);

      return clerk;
    } catch (error) {
      const errorMessage = error.message || 'Failed to load Clerk';
      console.error('Clerk loading failed:', errorMessage);

      // Set error state
      clerkError.set(error);
      isClerkLoaded.set(false);

      throw error;
    }
  }

  /**
   * Get authentication headers for API requests
   * Returns headers with Bearer token if user is authenticated
   * Returns empty object if Clerk not loaded or no active session
   *
   * @returns {Promise<object>} Headers object with Authorization and Content-Type
   *
   * @example
   * const headers = await clerkManager.getAuthHeaders();
   * fetch('/api/endpoint', { headers });
   */
  async getAuthHeaders() {
    try {
      const clerk = clerkInstance.get();

      if (!clerk) {
        return {};
      }

      if (!clerk.session) {
        return {};
      }

      const token = await clerk.session.getToken();

      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('Failed to get auth headers:', error);
      return {};
    }
  }
}

// Export singleton instance
export const clerkManager = new ClerkManager();
